import React, { useState, useCallback } from 'react';
import { Clipboard, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Divider } from './Divider';
import LinearGradient from 'react-native-linear-gradient';
import { HiveType } from '../screens/MyHives/useGetHivesQuery.gql';
import { applyTransparency } from '../../constants/colors';
import { HiveSize, PaymentStatus } from '../../constants/constants';
import { ChangeNameIcon } from '../../resources/svgComponents/ChangeNameIcon';
import { CopyIcon } from '../../resources/svgComponents/CopyIcon';

interface IMyHiveCardProps {
  hive: HiveType;
  isTeacher: boolean;
  onPress: (hive: HiveType) => void;
}
const MyHiveCard = ({ hive, isTeacher, onPress }: IMyHiveCardProps) => {
  const [hiveCopied, setHiveCopied] = useState(false);

  const handleCopyCode = useCallback(() => {
    Clipboard.setString(hive.code);
    setHiveCopied(true);
    setTimeout(() => setHiveCopied(false), 2000);
  }, [hive.code]);

  return (
    <>
      <LinearGradient
        colors={['#F1BF77', '#FFD322']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.gradientBorder}
      >
        <TouchableOpacity onPress={() => onPress(hive)}>
          <LinearGradient style={styles.hiveCard} colors={['#FEDD88', '#FEDD88']}>
            {hive.paymentStatus !== PaymentStatus.Active && (
              <>
                <Divider empty height={4} />
                <View style={styles.chipContainer}>
                  <Text style={styles.chipText}>Inactive</Text>
                </View>
                <Divider empty height={4} />
              </>
            )}
            <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
              <Text style={styles.hiveCardText} numberOfLines={2}>
                {hive.name}
              </Text>
            </View>
            <Divider empty height={4} />
            <Text style={styles.hiveType}>
              Size:{' '}
              {hive.size === HiveSize.ClassHive
                ? 'Class'
                : hive.size === HiveSize.FamilyHive
                ? 'Group'
                : 'Single'}
            </Text>
            {isTeacher && (
              <View style={{ position: 'relative', alignItems: 'center' }}>
                <View style={styles.hiveKeyRow}>
                  <Text style={styles.hiveType}>
                    Hive Key: <Text style={styles.hiveKeyBold}>{hive.code}</Text>
                  </Text>
                  <TouchableOpacity onPress={handleCopyCode} style={styles.copyButton}>
                    {hiveCopied
                      ? <Text style={styles.copiedIcon}>✓</Text>
                      : <CopyIcon size={14} />}
                  </TouchableOpacity>
                </View>
                {hiveCopied && <Text style={styles.copiedLabel}>Copied!</Text>}
              </View>
            )}
          </LinearGradient>
        </TouchableOpacity>
      </LinearGradient>
    </>
  );
};

export { MyHiveCard };

const styles = StyleSheet.create({
  hivesContainer: {
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 6,
    marginHorizontal: 6,
  },
  gradientBorder: {
    borderRadius: 16,
    paddingTop: 0,
    paddingBottom: 2.5,
    paddingRight: 2.5,
  },
  hiveType: {
    fontFamily: 'Secular One',
    color: '#A55A0F',
    textAlign: 'center',
    fontSize: 13,
  },
  hiveCard: {
    width: 280,
    borderRadius: 10,
    borderColor: '#FF9900',
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  hiveKeyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
  },
  hiveKeyBold: {
    fontFamily: 'Secular One',
    fontWeight: 'bold',
  },
  copyButton: {
    marginLeft: 6,
    padding: 2,
  },
  copiedIcon: {
    color: '#62B655',
    fontSize: 14,
    fontWeight: 'bold',
  },
  copiedLabel: {
    position: 'absolute',
    bottom: -20,
    fontSize: 11,
    fontFamily: 'Secular One',
    color: '#62B655',
    backgroundColor: '#F0FAF0',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    overflow: 'hidden',
  },
  hiveCardText: {
    fontFamily: 'Secular One',
    fontSize: 21,
    color: '#000000',
    maxWidth: 220,
  },
  chipContainer: {
    backgroundColor: '#ED4A4A',
    height: 18,
    width: 56,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
  },
  chipText: {
    fontFamily: 'Inter',
    color: 'white',
    fontSize: 8,
  },
});
